// components/AIBuilderPanel.tsx
'use client';

import { useState, useRef } from 'react';
import {
    X, Sparkles, Download, Send,
    Loader2, ChevronDown, ChevronUp,
    RefreshCw, ZoomIn
} from 'lucide-react';
import Image from 'next/image';

interface AIBuilderPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FormData {
    productName: string;
    description: string;
    material: string;
    finish: string;
    color: string;
    height: string;
    width: string;
    depth: string;
    weight: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerMessage: string;
}

const INITIAL_FORM: FormData = {
    productName: '',
    description: '',
    material: '',
    finish: '',
    color: '',
    height: '',
    width: '',
    depth: '',
    weight: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerMessage: '',
};

const MATERIALS = [
    'Teak Wood', 'Walnut Wood', 'Mango Wood',
    'Iron', 'Brass', 'Bronze', 'Steel',
    'Wood & Iron', 'Wood & Brass', 'Reclaimed Wood',
];

const FINISHES = [
    'Natural Polish', 'Matte Black', 'Antique Bronze',
    'Gold Leaf', 'Rustic Distressed', 'Lacquered',
    'Wax Polish', 'Painted',
];

export default function AIBuilderPanel({
    isOpen,
    onClose,
}: AIBuilderPanelProps) {
    const [form, setForm] = useState<FormData>(INITIAL_FORM);
    const [images, setImages] = useState<string[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDimensions, setShowDimensions] = useState(false);
    const [showCustomer, setShowCustomer] = useState(false);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const updateForm = (key: keyof FormData, value: string) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    // ── Generate Images ─────────────────────────────────
    const handleGenerate = async () => {
        if (!form.productName.trim()) {
            setError('Please enter a product name.');
            return;
        }

        setError('');
        setSuccess('');
        setIsGenerating(true);
        setImages([]);
        setSelectedImage(null);

        try {
            const res = await fetch('/api/generate-image', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Generation failed.');
            }

            setImages(data.images);
            setSelectedImage(data.images[0]);

        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to generate images.'
            );
        } finally {
            setIsGenerating(false);
        }
    };

    // ── Send Enquiry Email ───────────────────────────────
    const handleSendEnquiry = async () => {
        if (!form.customerEmail.trim()) {
            setError('Please enter your email to send an enquiry.');
            setShowCustomer(true);
            return;
        }

        setError('');
        setIsSending(true);

        try {
            const res = await fetch('/api/send-mail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    images: images.slice(0, 2),
                }),
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Failed to send enquiry.');
            }

            setSuccess('✅ Enquiry sent! We will contact you within 24 hours.');

        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to send enquiry.'
            );
        } finally {
            setIsSending(false);
        }
    };

    // ── Download Image ───────────────────────────────────
    const handleDownload = (image: string) => {
        const link = document.createElement('a');
        link.href = image;
        link.download = `${form.productName || 'product'}-concept.jpg`;
        link.click();
    };

    if (!isOpen) return null;

    return (
        <>
            {/* ── Backdrop ── */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40
                   lg:hidden"
                onClick={onClose}
            />

            {/* ── Panel ── */}
            <div
                ref={panelRef}
                className="
          fixed z-50 bg-white shadow-2xl flex flex-col
          /* Mobile: bottom sheet */
          bottom-0 left-0 right-0 h-[90vh] rounded-t-2xl
          /* Desktop: right sidebar */
          lg:top-0 lg:right-0 lg:bottom-0 lg:left-auto
          lg:h-full lg:w-[480px] lg:rounded-none
          transition-transform duration-300
        "
            >
                {/* ── Header ── */}
                <div className="flex items-center justify-between
                      px-5 py-4 border-b border-gray-100
                      bg-gradient-to-r from-purple-600 to-indigo-600
                      rounded-t-2xl lg:rounded-none flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-white" />
                        <h2 className="text-white font-bold text-lg">
                            AI Product Builder
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center
                     rounded-full bg-white/20 hover:bg-white/30
                     text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* ── Scrollable Content ── */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-5 space-y-5">

                        {/* Error / Success */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700
                            rounded-lg px-4 py-3 text-sm flex items-start gap-2">
                                <span>⚠️</span>
                                <span>{error}</span>
                            </div>
                        )}

                        {success && (
                            <div className="bg-green-50 border border-green-200 text-green-700
                            rounded-lg px-4 py-3 text-sm">
                                {success}
                            </div>
                        )}

                        {/* ── Product Name ── */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Product Name *
                            </label>
                            <input
                                type="text"
                                value={form.productName}
                                onChange={e => updateForm('productName', e.target.value)}
                                placeholder="e.g. Industrial Iron Coffee Table"
                                className="w-full border border-gray-200 rounded-lg px-4 py-3
                         text-sm focus:outline-none focus:ring-2
                         focus:ring-purple-400 focus:border-transparent
                         transition-all"
                            />
                        </div>

                        {/* ── Description ── */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                value={form.description}
                                onChange={e => updateForm('description', e.target.value)}
                                placeholder="Describe the style, design details, purpose..."
                                rows={3}
                                className="w-full border border-gray-200 rounded-lg px-4 py-3
                         text-sm focus:outline-none focus:ring-2
                         focus:ring-purple-400 focus:border-transparent
                         transition-all resize-none"
                            />
                        </div>

                        {/* ── Material & Finish ── */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Material
                                </label>
                                <select
                                    value={form.material}
                                    onChange={e => updateForm('material', e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-3
                           text-sm focus:outline-none focus:ring-2
                           focus:ring-purple-400 bg-white"
                                >
                                    <option value="">Select...</option>
                                    {MATERIALS.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Finish
                                </label>
                                <select
                                    value={form.finish}
                                    onChange={e => updateForm('finish', e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-3
                           text-sm focus:outline-none focus:ring-2
                           focus:ring-purple-400 bg-white"
                                >
                                    <option value="">Select...</option>
                                    {FINISHES.map(f => (
                                        <option key={f} value={f}>{f}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* ── Color ── */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Color / Tone
                            </label>
                            <input
                                type="text"
                                value={form.color}
                                onChange={e => updateForm('color', e.target.value)}
                                placeholder="e.g. Dark walnut with matte black metal"
                                className="w-full border border-gray-200 rounded-lg px-4 py-3
                         text-sm focus:outline-none focus:ring-2
                         focus:ring-purple-400 focus:border-transparent"
                            />
                        </div>

                        {/* ── Dimensions Accordion ── */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setShowDimensions(!showDimensions)}
                                className="w-full flex items-center justify-between
                         px-4 py-3 bg-gray-50 hover:bg-gray-100
                         text-sm font-semibold text-gray-700 transition-colors"
                            >
                                <span>📐 Dimensions (Optional)</span>
                                {showDimensions
                                    ? <ChevronUp className="w-4 h-4" />
                                    : <ChevronDown className="w-4 h-4" />
                                }
                            </button>

                            {showDimensions && (
                                <div className="p-4 grid grid-cols-2 gap-3">
                                    {(['height', 'width', 'depth', 'weight'] as const).map(key => (
                                        <div key={key}>
                                            <label className="block text-xs font-semibold
                                      text-gray-500 uppercase mb-1">
                                                {key}
                                            </label>
                                            <input
                                                type="text"
                                                value={form[key]}
                                                onChange={e => updateForm(key, e.target.value)}
                                                placeholder={
                                                    key === 'weight' ? 'e.g. 12kg' : 'e.g. 75cm'
                                                }
                                                className="w-full border border-gray-200 rounded-lg
                                 px-3 py-2 text-sm focus:outline-none
                                 focus:ring-2 focus:ring-purple-400"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Generate Button ── */}
                        <button
                            onClick={handleGenerate}
                            disabled={isGenerating}
                            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600
                       hover:from-purple-700 hover:to-indigo-700
                       disabled:opacity-60 disabled:cursor-not-allowed
                       text-white font-semibold py-4 rounded-xl
                       flex items-center justify-center gap-2
                       transition-all duration-200 shadow-lg
                       hover:shadow-purple-200 hover:shadow-xl"
                        >
                            {isGenerating ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Generating Concepts...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-5 h-5" />
                                    Generate AI Concepts
                                </>
                            )}
                        </button>

                        {/* ── Generated Images ── */}
                        {images.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-800 text-sm">
                                    ✨ Generated Concepts ({images.length})
                                </h3>

                                {/* Main Image */}
                                {selectedImage && (
                                    <div className="relative group rounded-xl overflow-hidden
                                border border-gray-200 shadow-md">
                                        <img
                                            src={selectedImage}
                                            alt="Generated product concept"
                                            className="w-full aspect-square object-cover"
                                        />

                                        {/* Overlay Actions */}
                                        <div className="absolute inset-0 bg-black/40 opacity-0
                                  group-hover:opacity-100 transition-opacity
                                  flex items-center justify-center gap-3">
                                            <button
                                                onClick={() => setLightboxImage(selectedImage)}
                                                className="bg-white text-gray-800 rounded-full p-3
                                 hover:bg-gray-100 transition-colors shadow-lg"
                                                title="View full size"
                                            >
                                                <ZoomIn className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDownload(selectedImage)}
                                                className="bg-white text-gray-800 rounded-full p-3
                                 hover:bg-gray-100 transition-colors shadow-lg"
                                                title="Download image"
                                            >
                                                <Download className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Thumbnail Grid */}
                                {images.length > 1 && (
                                    <div className="grid grid-cols-4 gap-2">
                                        {images.map((img, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setSelectedImage(img)}
                                                className={`
                          aspect-square rounded-lg overflow-hidden border-2
                          transition-all duration-200 hover:scale-105
                          ${selectedImage === img
                                                        ? 'border-purple-500 shadow-md shadow-purple-200'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                    }
                        `}
                                            >
                                                <img
                                                    src={img}
                                                    alt={`Concept ${i + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Regenerate */}
                                <button
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                    className="w-full border-2 border-purple-200 text-purple-600
                           hover:bg-purple-50 font-medium py-3 rounded-xl
                           flex items-center justify-center gap-2
                           transition-all text-sm"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    Regenerate
                                </button>
                            </div>
                        )}

                        {/* ── Customer Details Accordion ── */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setShowCustomer(!showCustomer)}
                                className="w-full flex items-center justify-between
                         px-4 py-3 bg-gray-50 hover:bg-gray-100
                         text-sm font-semibold text-gray-700 transition-colors"
                            >
                                <span>📧 Send Enquiry</span>
                                {showCustomer
                                    ? <ChevronUp className="w-4 h-4" />
                                    : <ChevronDown className="w-4 h-4" />
                                }
                            </button>

                            {showCustomer && (
                                <div className="p-4 space-y-3">
                                    <input
                                        type="text"
                                        value={form.customerName}
                                        onChange={e => updateForm('customerName', e.target.value)}
                                        placeholder="Your Name"
                                        className="w-full border border-gray-200 rounded-lg
                             px-4 py-3 text-sm focus:outline-none
                             focus:ring-2 focus:ring-purple-400"
                                    />
                                    <input
                                        type="email"
                                        value={form.customerEmail}
                                        onChange={e => updateForm('customerEmail', e.target.value)}
                                        placeholder="Your Email *"
                                        className="w-full border border-gray-200 rounded-lg
                             px-4 py-3 text-sm focus:outline-none
                             focus:ring-2 focus:ring-purple-400"
                                    />
                                    <input
                                        type="tel"
                                        value={form.customerPhone}
                                        onChange={e => updateForm('customerPhone', e.target.value)}
                                        placeholder="Phone / WhatsApp"
                                        className="w-full border border-gray-200 rounded-lg
                             px-4 py-3 text-sm focus:outline-none
                             focus:ring-2 focus:ring-purple-400"
                                    />
                                    <textarea
                                        value={form.customerMessage}
                                        onChange={e => updateForm('customerMessage', e.target.value)}
                                        placeholder="Additional message or requirements..."
                                        rows={3}
                                        className="w-full border border-gray-200 rounded-lg
                             px-4 py-3 text-sm focus:outline-none
                             focus:ring-2 focus:ring-purple-400 resize-none"
                                    />

                                    <button
                                        onClick={handleSendEnquiry}
                                        disabled={isSending}
                                        className="w-full bg-gray-900 hover:bg-gray-700
                             disabled:opacity-60 text-white font-semibold
                             py-3 rounded-xl flex items-center
                             justify-center gap-2 transition-all text-sm"
                                    >
                                        {isSending ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                Send Enquiry
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Bottom padding for mobile */}
                        <div className="h-6" />
                    </div>
                </div>
            </div>

            {/* ── Lightbox ── */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 z-[60] bg-black/90 flex items-center
                   justify-center p-4"
                    onClick={() => setLightboxImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 text-white bg-white/20
                     rounded-full p-2 hover:bg-white/30 transition-colors"
                        onClick={() => setLightboxImage(null)}
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <img
                        src={lightboxImage}
                        alt="Full size concept"
                        className="max-w-full max-h-full rounded-xl shadow-2xl
                     object-contain"
                        onClick={e => e.stopPropagation()}
                    />
                    <button
                        onClick={e => {
                            e.stopPropagation();
                            handleDownload(lightboxImage);
                        }}
                        className="absolute bottom-6 right-6 bg-white text-gray-800
                     font-medium px-5 py-3 rounded-xl flex items-center
                     gap-2 hover:bg-gray-100 transition-colors shadow-xl"
                    >
                        <Download className="w-5 h-5" />
                        Download
                    </button>
                </div>
            )}
        </>
    );
}